// Regras puras do histórico do ticket (modal "Logs"): frase de cada evento,
// agrupamento por dia, colapso de acessos repetidos e tempo entre etapas.
import { format, parseISO, differenceInMinutes, isToday, isYesterday } from "date-fns";
import { i18n } from "../../translate/i18n";

// tom visual por tipo (cores do AEROS): info/success/warning/error/purple/muted
export const TONES = {
  create: "purple", chatBot: "info", queue: "info", redirect: "info",
  open: "success", reopen: "success", access: "muted",
  transfered: "warning", receivedTransfer: "warning", pending: "warning",
  closed: "error", clientClosed: "error", nps: "purple",
};

export const toneOf = (type) => TONES[type] || "muted";

const first = (name) => (name || "").trim().split(" ")[0];

// Frase principal + quem/onde (chaves aeros.log.* — "xBy" quando há usuário).
const t = (k, o) => i18n.t(`aeros.log.${k}`, o);
const byOr = (user, key) => (user ? t(`${key}By`, { name: first(user) }) : t(key));
export const describeLog = (log) => {
  const user = log?.user?.name || "";
  const queue = log?.queue?.name || "";
  switch (log?.type) {
    case "create": return { text: t("create"), actor: user, meta: queue };
    case "chatBot": return { text: t("chatBot"), actor: "", meta: queue };
    case "queue": return { text: t("queue", { queue: queue || "—" }), actor: user, meta: "" };
    case "redirect": return { text: t("redirect", { queue: queue || t("otherQueue") }), actor: user, meta: "" };
    case "open": return { text: byOr(user, "open"), actor: user, meta: queue };
    case "reopen": return { text: byOr(user, "reopen"), actor: user, meta: queue };
    case "access": return { text: byOr(user, "access"), actor: user, meta: "" };
    case "transfered": return { text: byOr(user, "transfered"), actor: user, meta: queue };
    case "receivedTransfer": return { text: `${t("receivedBy", { name: first(user) || t("agent") })}${queue ? t("receivedIn", { queue }) : ""}`, actor: user, meta: queue };
    case "pending": return { text: byOr(user, "pending"), actor: user, meta: queue };
    case "closed": return { text: byOr(user, "closed"), actor: user, meta: queue };
    case "clientClosed": return { text: t("clientClosed"), actor: "", meta: "" };
    case "nps": return { text: t("nps"), actor: user, meta: "" };
    default: return { text: log?.type || t("event"), actor: user, meta: queue };
  }
};

// Acessos seguidos do mesmo usuário viram um só ("acessou 5x").
export const collapseAccess = (logs) => {
  const out = [];
  for (const log of logs) {
    const prev = out[out.length - 1];
    if (log.type === "access" && prev?.type === "access" && prev.userId === log.userId) {
      prev.count = (prev.count || 1) + 1;
      continue;
    }
    out.push({ ...log, count: 1 });
  }
  return out;
};

export const dayLabel = (iso) => {
  const d = parseISO(iso);
  if (isToday(d)) return t("today");
  if (isYesterday(d)) return t("yesterday");
  return format(d, "dd/MM/yyyy");
};

export const timeLabel = (iso) => format(parseISO(iso), "HH:mm");

// "+3 min" desde o evento anterior (lista em ordem cronológica).
export const elapsedLabel = (prevIso, iso) => {
  if (!prevIso) return "";
  const min = differenceInMinutes(parseISO(iso), parseISO(prevIso));
  if (min < 1) return "";
  if (min < 60) return `+${min} min`;
  if (min < 1440) return `+${Math.floor(min / 60)} h`;
  return `+${Math.floor(min / 1440)} d`;
};

// Entrada: logs como vêm da API (DESC). Saída: [{ day, items:[{...log, elapsed}] }]
// em ordem cronológica, com acessos colapsados e (opcional) ocultos.
export const buildTimeline = (logs, { showAccess = true } = {}) => {
  const asc = [...(logs || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const items = collapseAccess(asc).filter((l) => showAccess || l.type !== "access");
  const groups = [];
  let prev = null;
  for (const l of items) {
    const day = dayLabel(l.createdAt);
    let g = groups[groups.length - 1];
    if (!g || g.day !== day) { g = { day, items: [] }; groups.push(g); }
    g.items.push({ ...l, elapsed: elapsedLabel(prev, l.createdAt) });
    prev = l.createdAt;
  }
  return groups;
};

export const countByType = (logs) =>
  (logs || []).reduce((acc, l) => ({ ...acc, [l.type]: (acc[l.type] || 0) + 1 }), {});
